'use client'

import { useState, useMemo } from 'react'
import { cn } from '@paitox/ui/lib/utils'
import { Search, CreditCard, DollarSign, ArrowLeftRight, CheckCircle, AlertTriangle, HelpCircle } from 'lucide-react'
import { db, Payment as DbPayment } from '../lib/db'

export function PaymentsPage() {
  const [payments, setPayments] = useState<DbPayment[]>(() => db.getPayments())
  const [search, setSearch] = useState('')
  const [filterMethod, setFilterMethod] = useState<string>('all')

  const filtered = useMemo(() => {
    let list = payments
    if (search) {
      list = list.filter(p => p.clientName.toLowerCase().includes(search.toLowerCase()) || p.referenceName.toLowerCase().includes(search.toLowerCase()))
    }
    if (filterMethod !== 'all') {
      list = list.filter(p => p.method === filterMethod)
    }
    return list
  }, [payments, search, filterMethod])

  const totalCollected = useMemo(() => {
    return filtered
      .filter(p => p.status === 'PAID')
      .reduce((sum, p) => sum + p.amount, 0)
  }, [filtered])

  const refundPayment = (id: string) => {
    const updated = payments.map(p => p.id === id ? { ...p, status: 'REFUNDED' as const } : p)
    setPayments(updated)
    db.savePayments(updated)
  }

  const fmtCLP = (n: number) => `$${n.toLocaleString('es-CL')}`

  return (
    <div className="flex flex-col h-full overflow-hidden bg-[hsl(220,18%,96%)] p-4 gap-4">
      {/* Header with KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 shrink-0">
        <div className="bg-white rounded-2xl border border-slate-200/70 p-4 flex items-center gap-3 shadow-sm">
          <div className="h-10 w-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
            <DollarSign className="h-5 w-5" />
          </div>
          <div>
            <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">Recaudación Filtrada</p>
            <p className="text-lg font-extrabold text-slate-800">{fmtCLP(totalCollected)}</p>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200/70 p-4 flex items-center gap-3 shadow-sm">
          <div className="h-10 w-10 rounded-xl bg-cyan-50 text-cyan-600 flex items-center justify-center">
            <CreditCard className="h-5 w-5" />
          </div>
          <div>
            <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">Transacciones Totales</p>
            <p className="text-lg font-extrabold text-slate-800">{filtered.length} registradas</p>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200/70 p-4 flex items-center gap-3 shadow-sm">
          <div className="h-10 w-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center">
            <ArrowLeftRight className="h-5 w-5" />
          </div>
          <div>
            <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">Reembolsos Emitidos</p>
            <p className="text-lg font-extrabold text-slate-800">
              {filtered.filter(p => p.status === 'REFUNDED').length} transacciones
            </p>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap shrink-0">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por cliente o concepto..."
            className="w-full h-9 pl-9 pr-3 border border-slate-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-cyan-400/40"
          />
        </div>
        <select value={filterMethod} onChange={e => setFilterMethod(e.target.value)}
          className="h-9 px-3 border border-slate-200 rounded-xl text-xs font-medium text-slate-600 bg-white focus:outline-none">
          <option value="all">Todos los métodos</option>
          <option value="WEBPAY">Webpay (Transbank)</option>
          <option value="MERCADO_PAGO">Mercado Pago</option>
          <option value="BANK_TRANSFER">Transferencia</option>
          <option value="CASH">Efectivo</option>
          <option value="CARD_TERMINAL">Terminal Redelcom/POS</option>
        </select>
      </div>

      {/* List / Table */}
      <div className="flex-1 overflow-auto">
        <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm overflow-hidden">
          <table className="w-full text-left text-xs border-collapse">
            <thead>
              <tr className="bg-slate-50/75 text-slate-500 font-bold border-b border-slate-100">
                <th className="p-4">ID Transacción</th>
                <th className="p-4">Cliente</th>
                <th className="p-4">Concepto / Tipo</th>
                <th className="p-4">Método de Pago</th>
                <th className="p-4">Fecha Pago</th>
                <th className="p-4">Estado</th>
                <th className="p-4">Monto</th>
                <th className="p-4 text-right">Acción</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map(p => (
                <tr key={p.id} className="hover:bg-slate-50/50 transition">
                  <td className="p-4 font-mono text-[10px] text-slate-400">{p.id}</td>
                  <td className="p-4 font-semibold text-slate-800">{p.clientName}</td>
                  <td className="p-4">
                    <span className="capitalize text-slate-700 block font-medium">{p.referenceName}</span>
                    <span className="text-[9px] text-slate-400 capitalize">{p.type === 'appointment' ? 'Cita Agendada' : p.type === 'package' ? 'Bono/Paquete' : 'Gift Card'}</span>
                  </td>
                  <td className="p-4 font-medium text-slate-600">{p.method.replace('_', ' ')}</td>
                  <td className="p-4 text-slate-400">{new Date(p.createdAt).toLocaleDateString('es-CL')}</td>
                  <td className="p-4">
                    <span className={cn(
                      "px-2 py-0.5 rounded text-[10px] font-bold inline-flex items-center gap-1",
                      p.status === 'PAID' ? "bg-emerald-50 text-emerald-700 border border-emerald-100" : "bg-rose-50 text-rose-700 border border-rose-100"
                    )}>
                      {p.status === 'PAID' ? 'Aprobado' : 'Reembolsado'}
                    </span>
                  </td>
                  <td className="p-4 font-extrabold text-slate-800">{fmtCLP(p.amount)}</td>
                  <td className="p-4 text-right">
                    {p.status === 'PAID' ? (
                      <button onClick={() => refundPayment(p.id)}
                        className="px-2 py-1 text-[10px] font-bold rounded bg-slate-100 text-slate-600 hover:bg-rose-50 hover:text-rose-600 border border-transparent hover:border-rose-100 transition">
                        Reembolsar
                      </button>
                    ) : (
                      <span className="text-[10px] text-slate-400 font-semibold">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
